// The platforms a model call runs on. Must agree with
// src/inrange_agents/model_platforms.py: the ledger keys a call's model behind its
// platform's prefix, and a key with none is AWS.
import type { PlatformLegStatus } from "../api/hooks/useAgentConfigs";
import { formatUsd } from "./estimateRunCost";
import { formatDayLong } from "./formatRelative";

export const PLATFORMS = ["aws-bedrock", "anthropic-api", "gcp-vertex"] as const;
export type Platform = (typeof PLATFORMS)[number];

export const PLATFORM_LABEL: Record<Platform, string> = {
  "aws-bedrock": "AWS Bedrock",
  "anthropic-api": "Anthropic API",
  "gcp-vertex": "Google Vertex AI",
};

const PREFIX: Record<Exclude<Platform, "aws-bedrock">, string> = {
  "anthropic-api": "anthropic-api/",
  "gcp-vertex": "gcp-vertex/",
};

// A platform's place in PLATFORMS, so it keeps one colour whichever others are shown.
export const platformIndex = (platform: string): number => {
  const index = PLATFORMS.indexOf(platform as Platform);
  return index === -1 ? PLATFORMS.length : index;
};

export const platformLabel = (platform: string): string =>
  PLATFORM_LABEL[platform as Platform] ?? platform;

export const splitPlatform = (modelId: string): { platform: Platform; model: string } => {
  for (const [platform, prefix] of Object.entries(PREFIX)) {
    if (modelId.startsWith(prefix)) {
      return { platform: platform as Platform, model: modelId.slice(prefix.length) };
    }
  }
  return { platform: "aws-bedrock", model: modelId };
};

const LEG_STATE: Record<PlatformLegStatus["state"], string> = {
  in_use: "in use",
  cap_reached: "cap reached",
  not_configured: "not set up here",
  not_in_this_env: "production only",
  next: "next",
};

// "Anthropic API: $1,234.00 of $5,000.00 since 22 Oct 2026 (in use)".
export const planLegText = (leg: PlatformLegStatus): string => {
  const cap =
    leg.cap_usd === null
      ? ""
      : `: ${leg.spent_usd === null ? "spend unknown" : formatUsd(leg.spent_usd)} of ${formatUsd(
          leg.cap_usd,
        )}${leg.since ? ` since ${formatDayLong(leg.since)}` : ""}`;
  return `${leg.label}${cap} (${LEG_STATE[leg.state]})`;
};

// A plan worth describing: more than the one platform every config starts on.
export const planNote = (plan: PlatformLegStatus[] | undefined): string | null => {
  if (!plan || (plan.length === 1 && plan[0].platform === "aws-bedrock")) return null;
  return `Runs on ${plan.map(planLegText).join(", then ")}.`;
};
